// src/data/immigrationGuides/citizenshipGuide.js

const citizenshipGuide = {
    slug: "british-citizenship-guide",

    title: "British Citizenship Guide UK: Naturalisation Requirements and Process",

    seo: {
        metaTitle:
            "British Citizenship Guide UK: Naturalisation After ILR",
        metaDescription:
            "How to apply for British citizenship through naturalisation: residency requirements, ILR, the Life in the UK Test, English requirements and the ceremony.",
        keywords: [
            "British citizenship",
            "UK naturalisation",
            "British citizenship requirements",
            "citizenship after ILR",
            "UK passport after citizenship"
        ]
    },

    breadcrumbs: [
        {
            name: "Home",
            url: "/"
        },
        {
            name: "Study Guide",
            url: "/study-guide"
        },
        {
            name: "British Citizenship Guide",
            url: "/british-citizenship-guide"
        }
    ],

    introduction: `
British citizenship is the process through which eligible people can become
citizens of the United Kingdom.

Many people who have settled in the UK first obtain [Indefinite Leave to Remain](https://www.gov.uk/indefinite-leave-to-remain)
(ILR) or another form of permanent status before applying for citizenship.

The most common route for adults is naturalisation.
`,

    sections: [
        {
            heading: "What is British citizenship?",
            content: `
British citizenship gives a person nationality status in the United Kingdom.

British citizens may have rights including:

- the ability to apply for a British passport
- the right to live in the UK without immigration restrictions
- access to consular assistance when travelling overseas

Citizenship is different from Indefinite Leave to Remain. ILR is an immigration
status, while citizenship is nationality.
`
        },

        {
            heading: "What is naturalisation?",
            content: `
Naturalisation is the process through which an adult who is not automatically a
British citizen can apply to become a British citizen.

Applicants must meet eligibility requirements set by the Home Office.
`
        },

        {
            heading: "British citizenship requirements",
            content: `
Applicants normally need to satisfy several requirements, including:

- residence requirements
- immigration status requirements
- good character requirements
- English language requirements where applicable
- Life in the UK Test requirement where applicable

The exact requirements depend on the applicant's circumstances.
`
        },

        {
            heading: "Residence requirement",
            content: `
Most applicants applying through naturalisation must have lived in the UK for
the required qualifying period.

Applicants must also meet absence requirements and other residence conditions.

People married to British citizens may have different residence requirements.
`
        },

        {
            heading: "Good character requirement",
            content: `
Applicants must meet the good character requirement.

The Home Office considers relevant circumstances when assessing whether this
requirement has been met.

Applicants should provide accurate information and disclose relevant details
when required.
`
        },

        {
            heading: "Life in the UK Test",
            content: `
Most applicants need to pass the [Life in the UK Test](https://www.gov.uk/life-in-the-uk-test).

The test covers topics including:

- British history
- government and law
- traditions
- values and principles

A person who has already passed the test for ILR may normally use the same pass
result for a citizenship application.
`
        },

        {
            heading: "English language requirement",
            content: `
Many applicants must prove knowledge of English unless exempt.

Applicants may meet this requirement through accepted English qualifications,
eligible degrees, or another exemption under the Immigration Rules.
`
        },

        {
            heading: "Citizenship application process",
            content: `
The general process involves:

1. Checking eligibility
2. Completing the citizenship application
3. Providing supporting evidence
4. Providing biometric information where required
5. Receiving a Home Office decision
6. Attending a citizenship ceremony if successful

The exact process depends on individual circumstances.
`
        },

        {
            heading: "Citizenship ceremony",
            content: `
Successful applicants usually attend a citizenship ceremony before becoming
British citizens.

During the ceremony applicants make either:

- an oath of allegiance, or
- an affirmation of allegiance

They also make a pledge to respect the UK's rights, freedoms and laws.

After completing the ceremony, applicants receive a certificate of British
citizenship.
`
        },

        {
            heading: "Applying for a British passport",
            content: `
After becoming a British citizen, applicants can [apply for a British passport](https://www.gov.uk/apply-renew-passport).

The citizenship certificate is an important document used during the passport
application process.
`
        }
    ],

    checklist: [
        "Check eligibility for naturalisation",
        "Confirm residence requirements",
        "Review absence history",
        "Check immigration status requirements",
        "Pass the Life in the UK Test if required",
        "Meet English language requirements if required",
        "Prepare supporting documents",
        "Attend citizenship ceremony if approved"
    ],

    faqs: [
        {
            question: "Can I apply for British citizenship after ILR?",
            answer:
                "Many people apply for British citizenship after obtaining ILR if they meet the naturalisation requirements."
        },

        {
            question: "Do I need to take the Life in the UK Test again for citizenship?",
            answer:
                "If you have already passed the Life in the UK Test for a previous application, you normally do not need to take it again."
        },

        {
            question: "How long after ILR can I apply for citizenship?",
            answer:
                "Many applicants need to hold ILR for a period before applying, although different rules apply to spouses of British citizens."
        },

        {
            question: "Can I keep my previous nationality after becoming British?",
            answer:
                "The UK allows dual citizenship, but whether you can keep your previous nationality depends on the laws of your original country."
        },

        {
            question: "What happens after citizenship approval?",
            answer:
                "Successful applicants are invited to attend a citizenship ceremony before receiving their certificate of British citizenship."
        },

        // Fees below are the published Home Office figures effective 8 April
        // 2026 (naturalisation £1,709 + £130 ceremony = £1,839). Verify against
        // GOV.UK before every content review — these change most Aprils, and a
        // stale fee on this page is worse than no fee at all.
        {
            question: "How much does British citizenship cost in 2026?",
            answer:
                "Naturalisation costs £1,839 in total: £1,709 for the application plus a £130 citizenship ceremony fee. These are the Home Office fees effective 8 April 2026. You may also pay separately for biometrics appointments and optional document-checking or premium appointment services at a TLScontact or UKVCAS centre. Always check GOV.UK for the current figure before applying, as these fees are usually uprated each April."
        },

        {
            question: "Is priority processing available for citizenship, and is it worth it?",
            answer:
                "Priority service is sometimes offered as a paid add-on at the payment stage, after you have completed the application. Applicants report a surcharge of roughly £500 on top of the standard fee, though the amount and availability are not guaranteed and it is not always offered. Two things are worth knowing. Priority slots are limited and are typically released daily in the early hours, so you can save your application and return to pay when a slot appears. And priority shortens the queue, not the checks: if your case needs additional security or background checks, it can still take far longer than the advertised timescale. Priority is not a guarantee of a decision within any given period."
        },

        {
            question: "Do I still need to bring my passport if I upload documents beforehand?",
            answer:
                "Yes, bring it. Applicants regularly report that appointment centres scan the passport in person even when every page has already been uploaded as instructed by the document checklist. Uploading in advance is still worth doing because it usually makes the appointment quicker, but do not assume it replaces bringing the physical document. Take your passport, your appointment confirmation, and anything else the checklist lists as required."
        }
    ],

    relatedPages: [
        {
            title: "ILR Guide",
            url: "/ilr-guide"
        },
        {
            title: "Citizenship Ceremony Guide",
            url: "/citizenship-ceremony"
        },
        {
            title: "British Passport Application Guide",
            url: "/british-passport-application"
        },
        {
            title: "Dual Citizenship Guide",
            url: "/dual-citizenship"
        },
        {
            title: "Life in the UK Test Preparation",
            url: "/mock-exams"
        }
    ],

    officialSources: [
        {
            title: "GOV.UK - Apply for British Citizenship",
            url: "https://www.gov.uk/apply-citizenship-indefinite-leave-to-remain"
        },
        {
            title: "GOV.UK - Form AN Guidance",
            url: "https://www.gov.uk/government/publications/form-an-guidance"
        },
        {
            title: "GOV.UK - Citizenship Ceremonies",
            url: "https://www.gov.uk/citizenship-ceremonies"
        },
        {
            title: "GOV.UK - Life in the UK Test",
            url: "https://www.gov.uk/life-in-the-uk-test"
        }
    ],

    metadata: {
        contentType: "guide",
        category: "Immigration Guides",
        author: "Life in UK Coach",
        lastUpdated: "2026-07-20"
    }
};

export default citizenshipGuide;